/**
 * Excel host helpers — Office.js ExcelApi (Excel.run).
 * Reading, writing, region detection, charts, pivots, and agentic editing (applyOps).
 * ExcelApi 1.8+ required for pivots; rest works on 1.1+.
 */

import { ChartType, DocOp } from "../core/types";
import { unescapeLiteralText } from "./sanitize";

export interface SheetData {
  sheetName: string;
  address: string;
  headers: string[];
  rows: unknown[][];
  values: unknown[][];
}

/** An independently-detected contiguous data block on a sheet. */
export interface Region {
  /** Display address, e.g. "A1:F12". */
  address: string;
  /** Per-column spreadsheet letters (A, B, C…), aligned with `labels`. */
  letters: string[];
  /** Unique header labels — detected header text, else "列A/列B…". */
  labels: string[];
  /** Whether a header row was detected (vs. synthesized labels). */
  hasHeader: boolean;
  /** Data rows (header excluded), aligned with `labels` columns. */
  rows: unknown[][];
  /** First few data rows, for prompting. */
  sampleRows: unknown[][];
}

export interface SheetRegions {
  sheetName: string;
  regions: Region[];
}

export async function listSheets(): Promise<string[]> {
  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();
    return sheets.items.map((s) => s.name);
  });
}

export async function getActiveSheetName(): Promise<string> {
  return Excel.run(async (context) => {
    const s = context.workbook.worksheets.getActiveWorksheet();
    s.load("name");
    await context.sync();
    return s.name;
  });
}

export async function getUsedRangeData(sheetName?: string): Promise<SheetData> {
  return Excel.run(async (context) => {
    const sheet = sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet();
    const used = sheet.getUsedRangeOrNullObject(true);
    sheet.load("name");
    used.load(["values", "address", "rowCount", "columnCount"]);
    await context.sync();

    if (used.isNullObject) {
      return { sheetName: sheet.name, address: "", headers: [], rows: [], values: [] };
    }
    return toSheetData(sheet.name, used.address, used.values as unknown[][]);
  });
}

export async function getSelectedData(): Promise<SheetData> {
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    const sheet = range.worksheet;
    sheet.load("name");
    range.load(["values", "address"]);
    await context.sync();
    return toSheetData(sheet.name, range.address, range.values as unknown[][]);
  });
}

export interface FindCell {
  /** Sheet name containing the hit. */
  sheet: string;
  /** Cell address within the sheet, e.g. "B7". */
  address: string;
  /** Full cell address including sheet, e.g. "Sheet1!B7". */
  fullAddress: string;
  /** Row index within the used range (0-based). */
  row: number;
  /** Column index within the used range (0-based). */
  col: number;
  /** Cell value (always stringified for display). */
  value: string;
}

/** Find cells whose value contains (or equals) `query`. Searches the used
 *  range of every worksheet; returns up to `maxResults` hits. This lets the
 *  LLM answer "where is the row about project X?" or "list every cell with
 *  #N/A" without paging through giant sheets. */
export async function findCells(
  query: string,
  options?: { matchCase?: boolean; matchEntireCell?: boolean; sheet?: string; maxResults?: number }
): Promise<{ hits: FindCell[]; total: number }> {
  if (!query) return { hits: [], total: 0 };
  const max = Math.max(1, Math.min(200, options?.maxResults ?? 80));
  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    const targetSheetNames = options?.sheet
      ? sheets.items.map((s) => s.name).filter((n) => n === options.sheet)
      : sheets.items.map((s) => s.name);

    const hits: FindCell[] = [];
    let total = 0;
    const needle = options?.matchCase ? query : query.toLowerCase();

    for (const name of targetSheetNames) {
      if (hits.length >= max) break;
      const sheet = context.workbook.worksheets.getItem(name);
      const used = sheet.getUsedRangeOrNullObject(true);
      used.load(["values", "address", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
      await context.sync();
      if (used.isNullObject) continue;

      const grid = used.values as unknown[][];
      const baseRow = used.rowIndex;
      const baseCol = used.columnIndex;
      for (let r = 0; r < grid.length; r++) {
        if (hits.length >= max) break;
        for (let c = 0; c < grid[r].length; c++) {
          const v = grid[r][c];
          if (v === null || v === undefined) continue;
          const s = String(v);
          const hay = options?.matchCase ? s : s.toLowerCase();
          const matched = options?.matchEntireCell ? hay === needle : hay.indexOf(needle) !== -1;
          if (!matched) continue;
          total++;
          if (hits.length < max) {
            const cellAddr = `${colLetter(baseCol + c)}${baseRow + r + 1}`;
            hits.push({
              sheet: name,
              address: cellAddr,
              fullAddress: `${name}!${cellAddr}`,
              row: r,
              col: c,
              value: s,
            });
          }
        }
      }
    }
    return { hits, total };
  });
}

// ---------------------------------------------------------------------------
// Aggregation utilities — done on the client side (mechanical computation),
// so the LLM only sees the small result table instead of raw rows. This makes
// group-by / profile / duplicate-detection reliable on sheets of any size.
// ---------------------------------------------------------------------------

export interface ColumnProfile {
  /** 0-based column index within the used range (first column = header). */
  index: number;
  /** Header label (first-row text), or synthesized "列A/列B…". */
  label: string;
  /** Spreadsheet column letter. */
  letter: string;
  /** Detected data kind. */
  kind: "number" | "date" | "text" | "empty" | "mixed";
  /** Total non-empty values (excluding header). */
  nonEmpty: number;
  /** Empty / blank values. */
  missing: number;
  /** Distinct values count. */
  unique: number;
  /** Number-only stats (undefined when kind !== "number"). */
  numeric?: { min: number; max: number; sum: number; mean: number; median: number; std: number };
  /** Date-only stats (undefined when kind !== "date"). */
  dates?: { min: string; max: string };
  /** Most frequent values, top 5 (value → count). */
  topValues: { value: string; count: number }[];
}

function isNumeric(v: unknown): boolean {
  if (typeof v === "number") return true;
  if (typeof v === "string" && v.trim() !== "") return !Number.isNaN(Number(v.replace(/,/g, "")));
  return false;
}
function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  return Number(String(v).replace(/,/g, ""));
}
function median(nums: number[]): number {
  if (!nums.length) return 0;
  const a = [...nums].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}
function std(nums: number[], mean: number): number {
  if (nums.length < 2) return 0;
  const v = nums.reduce((s, n) => s + (n - mean) ** 2, 0) / (nums.length - 1);
  return Math.sqrt(v);
}

/** Read the used range of a sheet, detect a header row, and return a numeric
 *  profile for each column. Lets the LLM answer "describe this sheet" without
 *  paging through thousands of rows. */
export async function profileSheet(sheetName?: string): Promise<{ sheet: string; columns: ColumnProfile[]; rowCount: number }> {
  return Excel.run(async (context) => {
    const sheet = sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    const used = sheet.getUsedRangeOrNullObject(true);
    used.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();
    if (used.isNullObject) return { sheet: sheet.name, columns: [], rowCount: 0 };

    const grid = used.values as unknown[][];
    const baseCol = used.columnIndex;
    // Reuse the same header-detection heuristic as getSheetRegions: a row is a
    // header if it's mostly text and the next row brings numbers.
    const hasHeader = looksLikeHeader(grid.slice(0, Math.min(3, grid.length)));
    const headerRow = hasHeader ? grid[0] : [];
    const dataRows = hasHeader ? grid.slice(1) : grid;
    const colCount = used.columnCount;

    const columns: ColumnProfile[] = [];
    for (let c = 0; c < colCount; c++) {
      const letter = colLetter(baseCol + c);
      const label = hasHeader ? String(headerRow[c] ?? "") || `列${letter}` : `列${letter}`;
      const colValues = dataRows.map((r) => r[c]);
      const nonEmptyVals = colValues.filter((v) => !isEmpty(v));
      const nums = nonEmptyVals.filter(isNumeric).map(toNumber);
      const numRatio = nonEmptyVals.length ? nums.length / nonEmptyVals.length : 0;
      const dateCount = nonEmptyVals.filter((v) => v instanceof Date).length;

      let kind: ColumnProfile["kind"] = "text";
      if (nonEmptyVals.length === 0) kind = "empty";
      else if (dateCount > nonEmptyVals.length * 0.7) kind = "date";
      else if (numRatio > 0.85) kind = "number";
      else if (numRatio > 0.15) kind = "mixed";

      const counts = new Map<string, number>();
      for (const v of nonEmptyVals) {
        const k = String(v);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      const topValues = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([value, count]) => ({ value, count }));

      const profile: ColumnProfile = {
        index: c,
        label,
        letter,
        kind,
        nonEmpty: nonEmptyVals.length,
        missing: colValues.length - nonEmptyVals.length,
        unique: counts.size,
        topValues,
      };
      if (kind === "number" && nums.length) {
        const sum = nums.reduce((s, n) => s + n, 0);
        const mean = sum / nums.length;
        profile.numeric = { min: Math.min(...nums), max: Math.max(...nums), sum, mean, median: median(nums), std: std(nums, mean) };
      }
      if (kind === "date") {
        const ds = nonEmptyVals.filter((v) => v instanceof Date) as Date[];
        if (ds.length) {
          ds.sort((a, b) => a.getTime() - b.getTime());
          const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          profile.dates = { min: fmt(ds[0]), max: fmt(ds[ds.length - 1]) };
        }
      }
      columns.push(profile);
    }
    return { sheet: sheet.name, columns, rowCount: dataRows.length };
  });
}

export interface GroupByResult {
  sheet: string;
  /** The key column used for grouping. */
  keyColumn: { label: string; letter: string; index: number };
  /** Value columns that were aggregated (one entry per (group, valueCol)). */
  aggregations: {
    /** Group key (stringified). */
    group: string;
    /** Number of rows in this group. */
    count: number;
    /** Per-aggregated-column results: { label, op, result }. */
    values: { label: string; op: string; result: number }[];
  }[];
}

/** Group rows by `keyColumnIndex`, then compute one or more aggregations on
 *  the given numeric `valueColumnIndexes`. Reliable for any sheet size because
 *  aggregation happens client-side — the LLM never sees the raw rows. */
export async function groupBy(
  keyColumnIndex: number,
  valueColumnIndexes: number[],
  ops: ("sum" | "mean" | "count" | "min" | "max")[],
  options?: { sheet?: string }
): Promise<GroupByResult> {
  return Excel.run(async (context) => {
    const sheet = options?.sheet
      ? context.workbook.worksheets.getItem(options.sheet)
      : context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    const used = sheet.getUsedRangeOrNullObject(true);
    used.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();
    if (used.isNullObject) {
      return { sheet: sheet.name, keyColumn: { label: "", letter: "", index: keyColumnIndex }, aggregations: [] };
    }
    const grid = used.values as unknown[][];
    const baseCol = used.columnIndex;
    const hasHeader = looksLikeHeader(grid.slice(0, Math.min(3, grid.length)));
    const headerRow = hasHeader ? grid[0] : [];
    const dataRows = hasHeader ? grid.slice(1) : grid;

    // Build groups: key → array of row tuples (only the requested value cols).
    const groups = new Map<string, number[][]>();
    const colCount = used.columnCount;
    const safeKey = Math.max(0, Math.min(colCount - 1, keyColumnIndex));
    const valCols = valueColumnIndexes.filter((i) => i >= 0 && i < colCount);

    for (const row of dataRows) {
      const k = String(row[safeKey] ?? "").trim() || "（空）";
      const tuple = valCols.map((i) => {
        const v = row[i];
        return isNumeric(v) ? toNumber(v) : NaN;
      });
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(tuple);
    }

    // Aggregate.
    const aggregations: GroupByResult["aggregations"] = [];
    for (const [group, rows] of groups) {
      const count = rows.length;
      const values: { label: string; op: string; result: number }[] = [];
      for (let vi = 0; vi < valCols.length; vi++) {
        const colIdx = valCols[vi];
        const label = hasHeader ? String(headerRow[colIdx] ?? `列${colLetter(baseCol + colIdx)}`) : `列${colLetter(baseCol + colIdx)}`;
        const nums = rows.map((r) => r[vi]).filter((n) => !Number.isNaN(n));
        for (const op of ops) {
          let result = 0;
          if (op === "count") result = nums.length;
          else if (op === "sum") result = nums.reduce((s, n) => s + n, 0);
          else if (op === "mean") result = nums.length ? nums.reduce((s, n) => s + n, 0) / nums.length : 0;
          else if (op === "min") result = nums.length ? Math.min(...nums) : 0;
          else if (op === "max") result = nums.length ? Math.max(...nums) : 0;
          values.push({ label, op, result: Math.round(result * 1000) / 1000 });
        }
      }
      aggregations.push({ group, count, values });
    }

    return {
      sheet: sheet.name,
      keyColumn: {
        label: hasHeader ? String(headerRow[safeKey] ?? `列${colLetter(baseCol + safeKey)}`) : `列${colLetter(baseCol + safeKey)}`,
        letter: colLetter(baseCol + safeKey),
        index: safeKey,
      },
      aggregations,
    };
  });
}

export interface DuplicateReport {
  sheet: string;
  /** Columns whose combination was used as the duplicate key. */
  keyColumns: { label: string; letter: string; index: number }[];
  /** Distinct groups that contain >1 row. */
  groups: { key: string; count: number; /** Row indices (1-based) within the sheet used range. */ rows: number[] }[];
  /** Total rows involved in duplicates (= sum of groups' count). */
  totalDuplicateRows: number;
}

/** Find rows that share identical values across the given `keyColumns` (or all
 *  columns when keyColumns is empty). Returns each duplicate group with its
 *  member row indices. The LLM uses this to answer "which rows are duplicates?". */
export async function findDuplicates(
  keyColumnIndexes: number[],
  options?: { sheet?: string; maxGroups?: number }
): Promise<DuplicateReport> {
  return Excel.run(async (context) => {
    const sheet = options?.sheet
      ? context.workbook.worksheets.getItem(options.sheet)
      : context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    const used = sheet.getUsedRangeOrNullObject(true);
    used.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();
    if (used.isNullObject) {
      return { sheet: sheet.name, keyColumns: [], groups: [], totalDuplicateRows: 0 };
    }
    const grid = used.values as unknown[][];
    const baseCol = used.columnIndex;
    const baseRow = used.rowIndex;
    const hasHeader = looksLikeHeader(grid.slice(0, Math.min(3, grid.length)));
    const headerRow = hasHeader ? grid[0] : [];
    const dataRows = hasHeader ? grid.slice(1) : grid;
    const colCount = used.columnCount;
    const cols = (keyColumnIndexes.length ? keyColumnIndexes : Array.from({ length: colCount }, (_, i) => i))
      .filter((i) => i >= 0 && i < colCount);
    const keyCols = cols.map((i) => ({
      label: hasHeader ? String(headerRow[i] ?? `列${colLetter(baseCol + i)}`) : `列${colLetter(baseCol + i)}`,
      letter: colLetter(baseCol + i),
      index: i,
    }));

    const buckets = new Map<string, number[]>(); // key → 1-based sheet row indices
    dataRows.forEach((row, di) => {
      const k = cols.map((i) => String(row[i] ?? "")).join("\u0001");
      const sheetRow = baseRow + (hasHeader ? 1 : 0) + di + 1; // 1-based sheet row number
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k)!.push(sheetRow);
    });

    const maxGroups = Math.max(1, Math.min(500, options?.maxGroups ?? 100));
    const groups = [...buckets.entries()]
      .filter(([, rows]) => rows.length > 1)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, maxGroups)
      .map(([k, rows]) => ({ key: k.replace(/\u0001/g, " | "), count: rows.length, rows }));
    const totalDuplicateRows = groups.reduce((s, g) => s + g.count, 0);
    return { sheet: sheet.name, keyColumns: keyCols, groups, totalDuplicateRows };
  });
}

function toSheetData(sheetName: string, address: string, values: unknown[][]): SheetData {
  const headers = (values[0] ?? []).map((h) => String(h ?? ""));
  return { sheetName, address, headers, rows: values.slice(1), values };
}

/** Write an analysis write-up into a dedicated sheet, one paragraph per row. */
export async function writeSummary(title: string, paragraphs: string[]): Promise<string> {
  return Excel.run(async (context) => {
    const sheet = await getOrCreateSheet(context, "AI 分析");
    const usedRange = sheet.getUsedRangeOrNullObject(true);
    usedRange.load("rowCount");
    await context.sync();
    let row = usedRange.isNullObject ? 0 : usedRange.rowCount + 1;

    const titleCell = sheet.getCell(row, 0);
    titleCell.values = [[title]];
    titleCell.format.font.bold = true;
    titleCell.format.font.size = 13;
    row += 1;
    for (const p of paragraphs) {
      sheet.getCell(row, 0).values = [[p]];
      row += 1;
    }
    sheet.getRange("A:A").format.columnWidth = 480;
    sheet.activate();
    await context.sync();
    return "AI 分析";
  });
}

/**
 * Map our chart-type names to Excel's enum. This MUST be a function, not a
 * module-level constant: a top-level `Excel.ChartType.*` would read the global
 * `Excel` object at import time, which only exists in the Excel host. Reading it
 * here (lazily, inside createChart) keeps this module importable in Word/PowerPoint.
 */
function chartType(type: ChartType): Excel.ChartType {
  const map: Record<ChartType, Excel.ChartType> = {
    bar: Excel.ChartType.barClustered,
    column: Excel.ChartType.columnClustered,
    line: Excel.ChartType.line,
    pie: Excel.ChartType.pie,
    area: Excel.ChartType.area,
    scatter: Excel.ChartType.xyscatter,
  };
  return map[type];
}

// ---------------------------------------------------------------------------
// Data-region detection
// ---------------------------------------------------------------------------

function colLetter(index: number): string {
  let n = Math.max(0, Math.floor(index));
  let s = "";
  for (;;) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === "string" && v.trim() === "");
}
function isNumberLike(v: unknown): boolean {
  if (typeof v === "number") return true;
  if (typeof v === "string" && v.trim() !== "") return !Number.isNaN(Number(v.replace(/,/g, "")));
  return false;
}

/** Split a 2-D grid into contiguous data blocks: first by empty rows, then,
 *  within each row-band, by empty columns. Handles stacked AND side-by-side
 *  sub-tables on a single sheet. Bounds are local (0-based) inclusive. */
function detectRegionBounds(grid: unknown[][]): { r1: number; r2: number; c1: number; c2: number }[] {
  const out: { r1: number; r2: number; c1: number; c2: number }[] = [];
  const rows = grid.length;
  const cols = rows ? Math.max(...grid.map((r) => r.length)) : 0;
  if (!rows || !cols) return out;

  const rowHasData = (r: number) => grid[r] && grid[r].some((v) => !isEmpty(v));

  let r = 0;
  while (r < rows) {
    if (!rowHasData(r)) {
      r += 1;
      continue;
    }
    let re = r;
    while (re + 1 < rows && rowHasData(re + 1)) re += 1;

    // Within rows [r, re], split by empty columns.
    const colHasData = (c: number) => {
      for (let rr = r; rr <= re; rr++) if (!isEmpty(grid[rr]?.[c])) return true;
      return false;
    };
    let c = 0;
    while (c < cols) {
      if (!colHasData(c)) {
        c += 1;
        continue;
      }
      let ce = c;
      while (ce + 1 < cols && colHasData(ce + 1)) ce += 1;
      out.push({ r1: r, r2: re, c1: c, c2: ce });
      c = ce + 1;
    }
    r = re + 1;
  }
  return out;
}

/** Decide whether the first row of a block is a header (mostly text, with the
 *  following row carrying numbers). Returns true when row 0 should be a header. */
function looksLikeHeader(block: unknown[][]): boolean {
  if (block.length < 2) return false;
  const [row0, row1] = block;
  const textIn = (a: unknown[]) => a.filter((v) => typeof v === "string" && v.trim() !== "" && !isNumberLike(v)).length;
  const numIn = (a: unknown[]) => a.filter(isNumberLike).length;
  const width = Math.max(row0.length, 1);
  // Header if the first row is mostly text and the second row introduces numbers.
  if (numIn(row0) === 0 && numIn(row1) > 0) return true;
  return textIn(row0) >= width * 0.5 && textIn(row0) > textIn(row1) && numIn(row1) >= 1;
}

function uniqueLabels(raw: string[], letters: string[]): string[] {
  const seen = new Map<string, number>();
  return raw.map((label, i) => {
    let base = (label ?? "").replace(/\s+/g, " ").trim();
    if (!base) base = `列${letters[i]}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}(${letters[i]})`;
  });
}

/** Detect independent data regions on a sheet. Header-row position is inferred
 *  per region; when none is found the columns are labeled by their letters. */
export async function getSheetRegions(sheetName?: string): Promise<SheetRegions> {
  return Excel.run(async (context) => {
    const sheet = sheetName
      ? context.workbook.worksheets.getItem(sheetName)
      : context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    const used = sheet.getUsedRangeOrNullObject(true);
    used.load(["values", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();

    if (used.isNullObject) return { sheetName: sheet.name, regions: [] };

    const grid = used.values as unknown[][];
    const baseRow = used.rowIndex;
    const baseCol = used.columnIndex;

    const regions: Region[] = detectRegionBounds(grid).map(({ r1, r2, c1, c2 }) => {
      const block = grid.slice(r1, r2 + 1).map((row) => row.slice(c1, c2 + 1));
      const letters: string[] = [];
      for (let c = c1; c <= c2; c++) letters.push(colLetter(baseCol + c));

      const hasHeader = looksLikeHeader(block);
      const rawLabels = hasHeader ? block[0].map((v) => String(v ?? "")) : letters.map(() => "");
      const labels = uniqueLabels(rawLabels, letters);
      const dataRows = hasHeader ? block.slice(1) : block;

      const address = `${colLetter(baseCol + c1)}${baseRow + r1 + 1}:${colLetter(baseCol + c2)}${baseRow + r2 + 1}`;
      return {
        address,
        letters,
        labels,
        hasHeader,
        rows: dataRows,
        sampleRows: dataRows.slice(0, 8),
      };
    });

    return { sheetName: sheet.name, regions };
  });
}

// ---------------------------------------------------------------------------
// Chart / pivot from a concrete table (header row + data rows). No header-name
// matching — the caller resolves ordinals to real data before calling here.
// ---------------------------------------------------------------------------

/** Build a chart from a self-contained table ([labels, ...rows]). */
export async function chartFromTable(title: string, type: ChartType, table: unknown[][]): Promise<string> {
  if (table.length < 2 || (table[0]?.length ?? 0) < 2) throw new Error("数据不足以绘图（需至少 2 列、1 行数据）");
  return Excel.run(async (context) => {
    const target = await getOrCreateSheet(context, "AI 图表");
    const usedTarget = target.getUsedRangeOrNullObject(true);
    usedTarget.load("rowCount");
    await context.sync();
    const startRow = usedTarget.isNullObject ? 0 : usedTarget.rowCount + 2;

    const width = table[0].length;
    const dataRange = target.getRangeByIndexes(startRow, 0, table.length, width);
    dataRange.values = table;

    const chart = target.charts.add(chartType(type), dataRange, Excel.ChartSeriesBy.columns);
    chart.title.text = title;
    chart.setPosition(
      target.getRangeByIndexes(startRow, width + 1, 1, 1),
      target.getRangeByIndexes(startRow + 15, width + 8, 1, 1)
    );
    target.activate();
    await context.sync();
    return title;
  });
}

/** Build a PivotTable from a self-contained table. rows/cols/values are column
 *  ordinals into `table`. Header labels (table[0]) are unique by construction. */
export async function pivotFromTable(
  table: unknown[][],
  rowIdx: number[],
  colIdx: number[],
  valIdx: number[]
): Promise<string> {
  if (!rowIdx.length || !valIdx.length) throw new Error("透视表至少需要 1 个行字段和 1 个值字段");
  return Excel.run(async (context) => {
    // Write the source table to a dedicated sheet (cleared first).
    const srcSheet = await getOrCreateSheet(context, "AI 透视源");
    const prior = srcSheet.getUsedRangeOrNullObject(true);
    prior.load("isNullObject");
    await context.sync();
    if (!prior.isNullObject) {
      prior.clear(Excel.ClearApplyTo.all);
      await context.sync();
    }
    const width = table[0].length;
    const source = srcSheet.getRangeByIndexes(0, 0, table.length, width);
    source.values = table;

    const dest = await getOrCreateSheet(context, "AI 透视表");
    dest.pivotTables.load("items/name");
    await context.sync();
    dest.pivotTables.items.forEach((p) => p.delete());
    await context.sync();

    const labels = table[0].map((v) => String(v ?? ""));
    const pivot = dest.pivotTables.add("AIPivot", source, dest.getRange("A1"));
    for (const i of rowIdx) if (labels[i]) pivot.rowHierarchies.add(pivot.hierarchies.getItem(labels[i]));
    for (const i of colIdx) if (labels[i]) pivot.columnHierarchies.add(pivot.hierarchies.getItem(labels[i]));
    for (const i of valIdx) if (labels[i]) pivot.dataHierarchies.add(pivot.hierarchies.getItem(labels[i]));
    dest.activate();
    await context.sync();
    return "AI 透视表";
  });
}

export function supportsPivot(): boolean {
  return Office.context.requirements.isSetSupported("ExcelApi", "1.8");
}

/** Best-effort workbook author (from built-in document properties). */
export async function getAuthor(): Promise<string> {
  try {
    return await Excel.run(async (context) => {
      const props = context.workbook.properties;
      props.load("author");
      await context.sync();
      return props.author || "";
    });
  } catch {
    return "";
  }
}

async function getOrCreateSheet(context: Excel.RequestContext, name: string): Promise<Excel.Worksheet> {
  const existing = context.workbook.worksheets.getItemOrNullObject(name);
  existing.load("isNullObject");
  await context.sync();
  if (!existing.isNullObject) return existing;
  const sheet = context.workbook.worksheets.add(name);
  await context.sync();
  return sheet;
}

/** Execute a list of editing operations against the workbook. */
export async function applyOps(ops: DocOp[]): Promise<string[]> {
  const log: string[] = [];
  await Excel.run(async (context) => {
    for (const op of ops) {
      const kind = String(op.op || "");
      const sheetName = op.sheet ? String(op.sheet) : undefined;
      const sheet = sheetName
        ? context.workbook.worksheets.getItem(sheetName)
        : context.workbook.worksheets.getActiveWorksheet();

      if (kind === "writeRange") {
        const values = (op.values as unknown[][]) || [];
        const address = String(op.address || "A1");
        if (Array.isArray(values) && values.length) {
          const cols = Math.max(...values.map((r) => (Array.isArray(r) ? r.length : 0)));
          const data = values.map((r) => {
            const a = Array.isArray(r)
              ? r.slice().map((x) => (typeof x === "string" ? unescapeLiteralText(x) : x))
              : [];
            while (a.length < cols) a.push("");
            return a;
          });
          const start = sheet.getRange(address).getCell(0, 0);
          const range = start.getResizedRange(data.length - 1, cols - 1);
          range.values = data;
          log.push(`写入 ${sheetName ? sheetName + "!" : ""}${address}（${data.length}×${cols}）`);
        }
      } else if (kind === "putFormula") {
        const address = String(op.address || "A1");
        sheet.getRange(address).formulas = [[String(op.formula ?? "")]];
        log.push(`写入公式 ${address}`);
      } else if (kind === "setCell") {
        const address = String(op.address || "A1");
        const value = typeof op.value === "string" ? unescapeLiteralText(op.value) : op.value;
        sheet.getRange(address).values = [[value as string | number]];
        log.push(`设置单元格 ${address}`);
      } else {
        log.push(`忽略未知操作：${kind}`);
      }
      await context.sync();
    }
  });
  return log;
}
